import { useEffect, useState } from "react";
import { toast } from "sonner";

import { BottomSheet, ChalkActionButton, SectionTitle } from "./primitives";
import { api } from "../../lib/chalkApi";

const MODES = [
  { id: "move", label: "Move time" },
  { id: "delay", label: "Delay" },
  { id: "cancel", label: "Cancel" },
];

function toInput(localIso) {
  return (localIso || "").slice(0, 16);
}

function ConflictNote({ conflicts }) {
  if (!conflicts?.length) return null;
  return (
    <div data-testid="event-change-conflicts" style={{ marginTop: 10, fontSize: 13, color: "#9a6b00", background: "rgba(232,176,46,0.12)", borderRadius: 10, padding: "8px 12px" }}>
      Overlaps {conflicts.map((c) => c.title).join(", ")}. You can still save.
    </div>
  );
}

// Single-event Erase and Redraw: move the time, delay, or cancel. Families get the alert.
export default function EventChangeSheet({ isOpen, event, teamId, teamTimezone, onDismiss, onChanged }) {
  const [mode, setMode] = useState("move");
  const [when, setWhen] = useState("");
  const [note, setNote] = useState("");
  const [conflicts, setConflicts] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (isOpen && event) { setMode("move"); setWhen(toInput(event.local_time)); setNote(""); setConflicts([]); }
  }, [isOpen, event]);

  useEffect(() => {
    if (mode !== "move" || !when || !event || when === toInput(event.local_time)) { setConflicts([]); return; }
    const t = setTimeout(() => {
      api.checkConflicts(teamId, { start_local: when, exclude_event_id: event.id })
        .then((d) => setConflicts(d.conflicts || [])).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [mode, when, event, teamId]);

  async function save() {
    setBusy(true);
    try {
      if (mode === "move") await api.rescheduleEvent(event.id, when);
      else if (mode === "delay") await api.delayEvent(event.id, note || "Running late. Coach will update soon.");
      else await api.cancelEvent(event.id, note || "Coach will update soon.");
      toast.success(mode === "move" ? "Moved. Families are being told." : mode === "delay" ? "Delay sent to families." : "Cancelled. Families are being told.");
      onChanged && onChanged();
      onDismiss && onDismiss();
    } catch (e) {
      toast.error(e.message || "Couldn't change that event.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <BottomSheet isOpen={isOpen} title="Erase and Redraw" onDismiss={onDismiss}>
      {event && (
        <div data-testid="event-change-sheet">
          <div style={{ fontSize: 15, fontWeight: 500, color: "var(--chalk-board)" }}>{event.title}</div>
          <div role="tablist" style={{ display: "flex", gap: 8, margin: "14px 0" }}>
            {MODES.map((m) => (
              <button key={m.id} type="button" role="tab" aria-selected={mode === m.id} data-testid={`event-change-mode-${m.id}`}
                onClick={() => setMode(m.id)}
                style={{ flex: 1, padding: "9px 0", borderRadius: 999, fontSize: 13, cursor: "pointer",
                  border: "1px solid var(--chalk-border)", background: mode === m.id ? "var(--chalk-board)" : "transparent",
                  color: mode === m.id ? "#fff" : "var(--chalk-board)", transition: "background-color .2s" }}>
                {m.label}
              </button>
            ))}
          </div>
          {mode === "move" ? (
            <>
              <SectionTitle label={`New date and time${teamTimezone ? ` (team time, ${teamTimezone.replace(/_/g, " ")})` : ""}`} />
              <input type="datetime-local" className="chalk-input" data-testid="event-change-when" style={{ width: "100%", minWidth: 0 }} value={when} onChange={(e) => setWhen(e.target.value)} />
              <ConflictNote conflicts={conflicts} />
            </>
          ) : (
            <>
              <SectionTitle label="Message to families" />
              <textarea className="chalk-input" rows={3} data-testid="event-change-note" value={note} onChange={(e) => setNote(e.target.value)}
                placeholder={mode === "delay" ? "Running 20 minutes late." : "Field is closed today."} style={{ height: "auto", minHeight: 80, paddingTop: 10 }} />
            </>
          )}
          <div style={{ marginTop: 16 }}>
            <ChalkActionButton testId="event-change-save" variant={mode === "cancel" ? "danger" : "primary"} loading={busy} onClick={save}
              disabled={mode === "move" && !when}
              label={mode === "move" ? "Move and tell families" : mode === "delay" ? "Send delay" : "Cancel and tell families"} />
          </div>
        </div>
      )}
    </BottomSheet>
  );
}
